/**
 * PageGlow — the single, consistent ambient backdrop used behind every page hero.
 *
 * Replaces the old per-page hand-rolled radial glows + dot grids, which used
 * inconsistent positions, opacities and densities and made the site feel messy.
 * One refined brand-indigo aurora, anchored identically on every page, perfectly
 * clean (no texture).
 *
 * Render it as the first child of a `relative overflow-hidden` section.
 *
 *  - default : soft indigo aurora from the top (used on all standard pages)
 *  - home    : signature two-tone brand aurora for the landing hero
 *  - color   : tint the same aurora shape to a service's accent colour
 */
type PageGlowProps = {
  variant?: "default" | "home";
  /** Optional hex accent (e.g. "#5048E5") to tint the aurora for service pages. */
  color?: string;
  /** When true, adds a second, fainter glow further down the page so long pages don't go flat. */
  tall?: boolean;
};

export function PageGlow({ variant = "default", color, tall }: PageGlowProps) {
  let background: string;
  const fadeMask = tall
    ? "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.78) 8%, rgba(0,0,0,1) 18%, rgba(0,0,0,1) 72%, rgba(0,0,0,0.45) 88%, transparent 100%)"
    : "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.78) 9%, rgba(0,0,0,1) 22%, rgba(0,0,0,1) 58%, rgba(0,0,0,0.42) 78%, transparent 100%)";

  if (variant === "home") {
    background =
      "radial-gradient(85% 70% at 80% 28%, hsl(165 60% 35% / 0.16) 0%, transparent 55%)," +
      "radial-gradient(95% 80% at 16% -8%, hsl(243 75% 59% / 0.14) 0%, transparent 60%)";
    if (tall) {
      background += "," +
        "radial-gradient(90% 70% at 20% 85%, hsl(243 75% 59% / 0.06) 0%, transparent 55%)";
    }
  } else if (color) {
    background =
      `radial-gradient(115% 80% at 50% -12%, ${color}26 0%, ${color}0a 38%, transparent 64%)`;
    if (tall) {
      background += "," +
        `radial-gradient(100% 70% at 30% 90%, ${color}10 0%, ${color}03 40%, transparent 65%)`;
    }
  } else {
    background =
      "radial-gradient(115% 80% at 50% -12%, hsl(243 75% 59% / 0.13) 0%, hsl(243 75% 59% / 0.04) 38%, transparent 64%)";
    if (tall) {
      background += "," +
        "radial-gradient(100% 70% at 30% 90%, hsl(243 75% 59% / 0.06) 0%, hsl(243 75% 59% / 0.02) 40%, transparent 65%)";
    }
  }

  return (
    <div
      className="pointer-events-none absolute inset-0"
      aria-hidden="true"
      style={{
        background,
        maskImage: fadeMask,
        WebkitMaskImage: fadeMask,
      }}
    />
  );
}

export default PageGlow;
